import axios from 'axios';
import { servicesData } from '../data/servicesData';
import { packagesData } from '../data/packagesData';
import { loansData } from '../data/loansData';
import { schemesData } from '../data/schemesData';
import { blogData } from '../data/blogData';
import { insuranceData } from '../data/insuranceData';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to provide instant mock data without needing any running backend
function getStaticResponse(config) {
  const url = config.url || '';
  const method = (config.method || 'get').toLowerCase();

  if (method === 'get') {
    // Services
    if (url.startsWith('/api/services')) {
      const urlObj = new URL(url, 'http://localhost');
      const slug = urlObj.searchParams.get('slug');
      if (slug) {
        const found = servicesData.find(s => s.slug === slug || (s.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === slug);
        return { data: { service: found || servicesData[0] }, status: 200 };
      }
      return { data: { services: servicesData }, status: 200 };
    }

    // Packages
    if (url.startsWith('/api/packages')) {
      const urlObj = new URL(url, 'http://localhost');
      const slug = urlObj.searchParams.get('slug');
      if (slug) {
        const found = packagesData.find(p => p.slug === slug);
        return { data: { package: found || packagesData[0], packages: packagesData }, status: 200 };
      }
      return { data: { packages: packagesData }, status: 200 };
    }

    // Loans
    if (url.startsWith('/api/loans')) {
      const cleanPath = url.split('?')[0];
      const parts = cleanPath.split('/').filter(Boolean);
      const slug = parts.length > 2 ? parts[2] : null;
      if (slug && slug !== 'seed' && slug !== 'check-eligibility') {
        const found = loansData.find(l => l.slug === slug || l._id === slug || l.name.toLowerCase().includes(slug.replace(/-/g, ' ')));
        return { data: { loan: found || loansData[0] }, status: 200 };
      }
      return { data: { loans: loansData }, status: 200 };
    }

    // Schemes
    if (url.startsWith('/api/schemes')) {
      const cleanPath = url.split('?')[0];
      const parts = cleanPath.split('/').filter(Boolean);
      const slug = parts.length > 2 ? parts[2] : null;
      if (slug === 'matched') {
        return {
          data: {
            matched: schemesData.slice(0, 8).map((s, idx) => ({
              ...s,
              scheme: s,
              matchScore: 95 - (idx * 3),
              recommendedReason: 'Direct match for MSME enterprise criteria'
            })),
            isPremium: true
          },
          status: 200
        };
      }
      if (slug && slug !== 'seed' && slug !== 'check-matches') {
        const found = schemesData.find(s => s.slug === slug);
        return { data: { scheme: found || schemesData[0] }, status: 200 };
      }
      return { data: { schemes: schemesData }, status: 200 };
    }

    // Blog
    if (url.startsWith('/api/blog')) {
      const cleanPath = url.split('?')[0];
      const parts = cleanPath.split('/').filter(Boolean);
      const slug = parts.length > 2 ? parts[2] : null;
      if (slug && slug !== 'seed') {
        const found = blogData.find(b => b.slug === slug);
        return { data: found || blogData[0], status: 200 };
      }
      return { data: blogData, status: 200 };
    }

    // Insurance Products
    if (url.startsWith('/api/insurance/products')) {
      const cleanPath = url.split('?')[0];
      const parts = cleanPath.split('/').filter(Boolean);
      const slug = parts.length > 3 ? parts[3] : null;
      if (slug) {
        const found = insuranceData.find(p => p.slug === slug || p._id === slug);
        return { data: { product: found || insuranceData[0] }, status: 200 };
      }
      const urlObj = new URL(url, 'http://localhost');
      const cat = urlObj.searchParams.get('category');
      const filtered = (cat && cat !== 'all') ? insuranceData.filter(p => p.category === cat) : insuranceData;
      return { data: { products: filtered }, status: 200 };
    }

    // Forum / Categories
    if (url.startsWith('/api/forum/categories')) {
      return {
        data: {
          categories: [
            { _id: 'cat1', name: 'General MSME', slug: 'general-msme', description: 'General business discussions', postCount: 12 },
            { _id: 'cat2', name: 'GST & Taxes', slug: 'gst-taxes', description: 'Taxation queries and GST filing support', postCount: 28 },
            { _id: 'cat3', name: 'Business Loans', slug: 'business-loans', description: 'Funding, mudra loans, and subsidy tips', postCount: 34 }
          ]
        },
        status: 200
      };
    }

    if (url.startsWith('/api/forum/posts')) {
      return { data: { posts: [] }, status: 200 };
    }

    // Tenders
    if (url.startsWith('/api/tenders')) {
      return { data: { tenders: [], total: 0 }, status: 200 };
    }

    // User profile or gamification (guest mode)
    if (url.startsWith('/api/users/profile') || url.startsWith('/api/gamification/me')) {
      return {
        data: {
          user: { name: 'Guest Entrepreneur', email: '', role: 'customer' },
          points: 100,
          tier: 'Silver'
        },
        status: 200
      };
    }
  }

  // Handle POST/PUT actions (Leads, quote submissions, contacts, eligibility)
  if (method === 'post' || method === 'put') {
    if (url.startsWith('/api/loans/check-eligibility')) {
      return {
        data: {
          eligibilityScore: 88,
          estimatedLoanAmount: 25,
          matches: loansData.slice(0, 4).map(l => ({
            _id: l._id,
            name: l.name,
            slug: l.slug,
            description: l.description,
            matchScore: 92,
            estimatedLakhs: 20
          }))
        },
        status: 200
      };
    }

    if (url.startsWith('/api/insurance/apply')) {
      return {
        data: {
          success: true,
          application: {
            _id: 'app_' + Date.now(),
            quoteAmount: 4999,
            additionalData: { partnerReference: 'POL-' + Math.floor(100000 + Math.random() * 900000) }
          }
        },
        status: 200
      };
    }

    return {
      data: {
        success: true,
        message: 'Request processed successfully. Connect with us on WhatsApp.',
      },
      status: 200
    };
  }

  return null;
}

// Request interceptor: If no backend is configured or if requested path is a known static dataset, resolve directly
api.interceptors.request.use(
  async (config) => {
    // If backend is not explicitly requested or we want pure standalone WhatsApp-first architecture
    const staticRes = getStaticResponse(config);
    if (staticRes) {
      config.adapter = () => Promise.resolve({
        data: staticRes.data,
        status: staticRes.status,
        statusText: 'OK',
        headers: {},
        config,
      });
      return config;
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor fallback
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If a request failed against the backend, fallback to static mock data seamlessly
    const staticRes = getStaticResponse(error.config || {});
    if (staticRes) {
      return Promise.resolve({
        data: staticRes.data,
        status: staticRes.status,
        statusText: 'OK',
        headers: {},
        config: error.config,
      });
    }
    return Promise.reject(error);
  }
);

export default api;
